import type { CSSProperties } from "react";
import { forwardRef } from "react";
import type { TextItem } from "./Search.types";

// TODO: Find a way to figure out the text height, currently we use the standard 1.75.
const BASELINE_OFFSET_MULTIPLIER = 1.75;

export type CustomHighlightArea = {
  pageIndex: number;
  pageHeight: number;
  pageWidth: number;
  textItems: {
    item: TextItem;
    matchStartInItem: number;
    matchEndInItem: number;
  }[];
};

type CustomHighlightLayerProps = {
  totalNoOfPages: number;
  pageDimensions: {
    [pageIndex: number]: {
      height: number;
      width: number;
    };
  };
  highlightAreas: CustomHighlightArea[];
  scale: number;
};

export const CustomHighlightLayer = forwardRef<
  HTMLDivElement,
  CustomHighlightLayerProps
>(({ highlightAreas, pageDimensions, totalNoOfPages, scale }, ref) => {
  return (
    <div
      ref={ref}
      style={{
        display: "flex",
        position: "absolute",
        top: "1rem",
        flexDirection: "column",
        gap: "1rem",
        pointerEvents: "none"
      }}
    >
      {Array.from(new Array(totalNoOfPages)).map((_, pageIndex) => {
        const pageHighlights = highlightAreas.filter(
          h => h.pageIndex === pageIndex
        );
        const pageElement = document.querySelector(
          `div.react-pdf__Page[data-page-number="${pageIndex + 1}"]`
        ) as HTMLDivElement;

        const actualHeight =
          pageElement?.clientHeight || pageDimensions[pageIndex]?.height || 0;
        const actualWidth =
          pageElement?.clientWidth || pageDimensions[pageIndex]?.width || 0;

        return (
          <div
            key={pageIndex}
            style={{
              position: "absolute", // Change from relative to absolute
              top: pageElement?.offsetTop || 0, // Get actual position
              left: pageElement?.offsetLeft || 0, // Get actual position
              width: actualWidth,
              height: actualHeight
            }}
          >
            {pageHighlights.map((highlight, idx) =>
              highlight.textItems.map((textItem, itemIdx) => {
                const style = calculateHighlightStyle(
                  textItem.item,
                  textItem.matchStartInItem,
                  textItem.matchEndInItem,
                  scale,
                  actualHeight, // Use pageDimensions instead!
                  highlight.pageIndex
                );
                return (
                  <div
                    key={`${idx}-${itemIdx}`}
                    style={style}
                    title={textItem.item.str.substring(
                      textItem.matchStartInItem,
                      textItem.matchEndInItem
                    )}
                  />
                );
              })
            )}
          </div>
        );
      })}
    </div>
  );
});

/**
 * Calculate highlight position from PDF.js transform matrix
 * Transform matrix: [scaleX, skewY, skewX, scaleY, translateX, translateY]
 */
function calculateHighlightStyle(
  textItem: TextItem,
  matchStartInItem: number,
  matchEndInItem: number,
  pageScale: number,
  pageHeight: number,
  pageIndex: number
): CSSProperties {
  if (!("transform" in textItem) || !textItem.transform) {
    return {};
  }

  const transform = textItem.transform;
  const [, , , scaleY, translateX, translateY] = transform;

  // Font size from the transformation matrix
  const fontSize = scaleY;

  // Calculate character width (approximation)
  const fullTextWidth = textItem.width || 0;
  const textLength = textItem.str.length;
  const avgCharWidth = textLength > 0 ? fullTextWidth / textLength : 0;

  // Calculate the width of text before the match
  const beforeMatchWidth = matchStartInItem * avgCharWidth;
  // Calculate the width of the matched text
  const matchedTextLength = matchEndInItem - matchStartInItem;
  const matchWidth = matchedTextLength * avgCharWidth;

  const pdfTop = pageHeight - translateY - fontSize; // Calculate in PDF points

  // Same for other dimensions
  const scaledLeft = (translateX + beforeMatchWidth) * pageScale;
  const scaledWidth = matchWidth * pageScale;
  const scaledHeight = fontSize * pageScale;
  const scaledTop =
    pdfTop * pageScale - scaledHeight * BASELINE_OFFSET_MULTIPLIER; // Then scale to pixels

  return {
    position: "absolute",
    backgroundColor: "rgba(255, 235, 59, 0.5)",
    border: "1px solid rgba(255, 193, 7, 0.8)",
    left: `${scaledLeft}px`,
    top: `${scaledTop}px`,
    width: `${scaledWidth}px`,
    height: `${scaledHeight}px`,
    pointerEvents: "none"
  };
}
